<section class="overview-page dashboard">
  <app-page-header
    [title]="'dashboard.projects.headline' | translate"
    [subtitle]="'dashboard.projects.subtitle' | translate"
  >
    <ng-container right>
      <app-stat-group [items]="projectStats()" />
      @if (isPlatformAdmin()) {
        <button
          mat-flat-button
          color="primary"
          type="button"
          class="dashboard__create-btn"
          data-testid="create-project-btn"
          (click)="onNavigateToCreate()"
        >
          <mat-icon aria-hidden="true">add</mat-icon>
          {{ 'dashboard.addProject' | translate }}
        </button>
      }
    </ng-container>
  </app-page-header>

  <div class="overview-page__toolbar">
    <app-search-bar
      [placeholder]="'dashboard.projects.search' | translate"
      [value]="projectSearchTerm()"
      (valueChange)="onProjectSearchChange($event)"
    />
    <button
      type="button"
      class="dropdown-chip"
      [class.dropdown-chip--active]="statusFilter() !== null"
      [matMenuTriggerFor]="statusMenu"
      [attr.aria-label]="'dashboard.filter.status' | translate"
    >
      @if (statusFilter()) {
        {{ 'dashboard.filter.status' | translate }}: {{ 'dashboard.filter.status_' + statusFilter() | translate }}
      } @else {
        {{ 'dashboard.filter.status' | translate }}
      }
      <span class="dropdown-chip__caret" aria-hidden="true">▾</span>
    </button>
    <mat-menu #statusMenu="matMenu">
      <button mat-menu-item (click)="setStatusFilter(null)">
        {{ 'dashboard.filter.allStatuses' | translate }}
      </button>
      <button mat-menu-item (click)="setStatusFilter('entwurf')">
        {{ 'dashboard.filter.status_entwurf' | translate }}
      </button>
      <button mat-menu-item (click)="setStatusFilter('aktiv')">
        {{ 'dashboard.filter.status_aktiv' | translate }}
      </button>
      <button mat-menu-item (click)="setStatusFilter('pausiert')">
        {{ 'dashboard.filter.status_pausiert' | translate }}
      </button>
      <button mat-menu-item (click)="setStatusFilter('archiviert')">
        {{ 'dashboard.filter.status_archiviert' | translate }}
      </button>
    </mat-menu>
    <button
      type="button"
      class="dropdown-chip"
      [class.dropdown-chip--active]="leadFilter() !== null"
      [matMenuTriggerFor]="responsibleMenu"
      [attr.aria-label]="'dashboard.filter.responsible' | translate"
    >
      @if (leadFilter()) {
        {{ getLeadName(leadFilter()!) }}
      } @else {
        {{ 'dashboard.filter.responsible' | translate }}
      }
      <span class="dropdown-chip__caret" aria-hidden="true">▾</span>
    </button>
    <mat-menu #responsibleMenu="matMenu">
      <button mat-menu-item (click)="setResponsibleFilter(null)">
        {{ 'dashboard.filter.allResponsible' | translate }}
      </button>
      @for (user of responsibleUsers(); track user.id) {
        <button mat-menu-item (click)="setResponsibleFilter(user.id)">{{ user.name }}</button>
      }
    </mat-menu>
    <div class="dashboard__zeitraum-wrapper" [formGroup]="zeitraumForm">
      <button
        type="button"
        class="dropdown-chip"
        [class.dropdown-chip--active]="isZeitraumActive()"
        (click)="isZeitraumActive() ? clearZeitraumFilter() : zeitraumPicker.open()"
        [attr.aria-label]="'dashboard.filter.period' | translate"
      >
        @if (isZeitraumActive()) {
          {{ 'dashboard.filter.period' | translate }}: {{ zeitraumStart() | date: 'dd.MM.yy' }} –
          {{ zeitraumEnd() | date: 'dd.MM.yy' }}
          <mat-icon class="dropdown-chip__close" aria-hidden="true">close</mat-icon>
        } @else {
          {{ 'dashboard.filter.period' | translate }}
          <span class="dropdown-chip__caret" aria-hidden="true">▾</span>
        }
      </button>

      <!-- Hidden anchor for the calendar overlay -->
      <mat-form-field class="dashboard__hidden-date-anchor">
        <mat-date-range-input [rangePicker]="zeitraumPicker">
          <input matStartDate formControlName="start" />
          <input matEndDate formControlName="end" />
        </mat-date-range-input>
        <mat-date-range-picker #zeitraumPicker />
      </mat-form-field>
    </div>

    <div class="overview-page__toolbar-spacer"></div>
    <span class="overview-page__count-label">
      {{ filteredProjects().length }} {{ 'dashboard.projects.countLabel' | translate }}
    </span>
  </div>

  @if (projectsLoading()) {
    <div class="overview-page__loading">
      <mat-spinner diameter="32"></mat-spinner>
    </div>
  } @else if (loadError()) {
    <p class="overview-page__hint">{{ 'dashboard.loadProjectsError' | translate }}</p>
  } @else if (filteredProjects().length === 0) {
    <p class="overview-page__hint">
      {{ (projects().length === 0 ? 'dashboard.noProjects' : 'dashboard.projects.noResults') | translate }}
    </p>
  } @else {
    <mat-card class="overview-page__table-card">
      <div class="overview-page__table-scroll" appScrollContainer>
        <table
          mat-table
          [dataSource]="pagedProjects()"
          class="overview-page__table"
          matSort
          (matSortChange)="onSortChange($event)"
        >
          <ng-container matColumnDef="name">
            <th mat-header-cell *matHeaderCellDef scope="col" mat-sort-header="name">
              {{ 'dashboard.projects.table.name' | translate }}
            </th>
            <td mat-cell *matCellDef="let project">
              <div class="cell-name">
                @let iconDef = getProjectIconDef(project);
                <div
                  class="cell-name__icon"
                  [style.color]="iconDef.color"
                  [style.background]="'color-mix(in srgb, ' + iconDef.color + ' 10%, transparent)'"
                >
                  <mat-icon aria-hidden="true">{{ iconDef.matIcon }}</mat-icon>
                </div>
                <div>
                  <div class="cell-name__primary">{{ project.name }}</div>
                  <div class="cell-name__secondary">
                    {{ project.testCaseCount ?? 0 }}
                    {{
                      ((project.testCaseCount ?? 0) === 1
                        ? 'dashboard.projects.testCasesSuffixOne'
                        : 'dashboard.projects.testCasesSuffix'
                      ) | translate
                    }}
                    · {{ project.runningExecutionCount ?? 0 }}
                    {{
                      ((project.runningExecutionCount ?? 0) === 1
                        ? 'dashboard.projects.runningExecutionsSuffixOne'
                        : 'dashboard.projects.runningExecutionsSuffix'
                      ) | translate
                    }}
                  </div>
                </div>
              </div>
            </td>
          </ng-container>

          <ng-container matColumnDef="responsible">
            <th mat-header-cell *matHeaderCellDef scope="col" mat-sort-header="responsible">
              {{ 'dashboard.projects.table.responsible' | translate }}
            </th>
            <td mat-cell *matCellDef="let project">
              <div class="cell-responsible">
                @if (projectLeadIds(project).length > 0) {
                  <app-avatar-group [names]="projectLeadNames(project)" [size]="24" [maxVisible]="3" />
                } @else {
                  <span class="cell-responsible__empty">—</span>
                }
              </div>
            </td>
          </ng-container>

          <ng-container matColumnDef="status">
            <th mat-header-cell *matHeaderCellDef scope="col" mat-sort-header="status">
              {{ 'dashboard.projects.table.status' | translate }}
            </th>
            <td mat-cell *matCellDef="let project">
              <app-status-pill [kind]="getProjectStatus(project)" [label]="getStatusLabel(project)" />
            </td>
          </ng-container>

          <ng-container matColumnDef="progress">
            <th mat-header-cell *matHeaderCellDef scope="col">{{ 'dashboard.projects.table.progress' | translate }}</th>
            <td mat-cell *matCellDef="let project">
              <div class="cell-progress">
                <div class="cell-progress__label">{{ getProjectProgress(project) }}%</div>
                <div class="cell-progress__bar">
                  <div
                    class="cell-progress__fill"
                    [style.width.%]="getProjectProgress(project)"
                    [attr.aria-valuenow]="getProjectProgress(project)"
                    aria-valuemin="0"
                    aria-valuemax="100"
                    role="progressbar"
                  ></div>
                </div>
              </div>
            </td>
          </ng-container>

          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef scope="col" class="col-actions">
              {{ 'dashboard.projects.table.manage' | translate }}
            </th>
            <td mat-cell *matCellDef="let project" (click)="$event.stopPropagation()">
              @if (canManageProject(project)) {
                <div class="overview-page__action-cell">
                  <button
                    class="overview-page__action-btn overview-page__action-btn--accent"
                    type="button"
                    (click)="onNavigateToEdit(project)"
                    [attr.aria-label]="'dashboard.projects.edit' | translate"
                  >
                    <mat-icon fontSet="material-icons-outlined" aria-hidden="true">edit</mat-icon>
                  </button>
                  <button
                    class="overview-page__action-btn overview-page__action-btn--danger"
                    type="button"
                    (click)="onOpenDeleteModal(project)"
                    [attr.aria-label]="'dashboard.projects.delete' | translate"
                  >
                    <mat-icon fontSet="material-icons-outlined" aria-hidden="true">delete</mat-icon>
                  </button>
                </div>
              } @else {
                <span class="dashboard__readonly-label">{{ memberLabelKey(project) | translate }}</span>
              }
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="projectTableColumns"></tr>
          <tr
            mat-row
            *matRowDef="let row; columns: projectTableColumns"
            class="overview-page__row"
            role="link"
            tabindex="0"
            [attr.aria-label]="('dashboard.projects.openProject' | translate) + ': ' + row.name"
            (click)="onNavigateToProject(row)"
            (keydown.enter)="onRowKeydown($event, row)"
            (keydown.space)="onRowKeydown($event, row)"
          ></tr>
        </table>
      </div>
    </mat-card>
    <app-overview-pagination
      [pageIndex]="projectPageIndex()"
      [pageSize]="projectPageSize()"
      [pageSizeOptions]="projectPageSizeOptions"
      [totalCount]="filteredProjects().length"
      (pageIndexChange)="projectPageIndex.set($event)"
      (pageSizeChange)="onPageSizeSelectChange($event)"
    />
  }
</section>

<app-confirm-dialog
  [visible]="showDeleteProject()"
  [title]="'dashboard.deleteProject.title' | translate"
  [message]="deleteProjectMessage()"
  [confirmLabel]="'dashboard.deleteProject.confirm' | translate"
  [cancelLabel]="'dashboard.deleteProject.cancel' | translate"
  [busy]="deleteProjectBusy()"
  (confirmed)="onConfirmDelete()"
  (cancelled)="onCancelDelete()"
></app-confirm-dialog>
